import { useQuery } from '@tanstack/react-query'
import { useEffect } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'

import { ERROR_MESSAGES } from '~/constants/messages'
import useCall from '~/hooks/useCall'
import useHandleError from '~/hooks/useHandleError'
import counter from '~/services/counter'
import { SocketReqJoinRoom, StartQueueReqData } from '~/types/call'
import { generateNumberOptions, transformBranches, transformCounters } from '~/utils/dataTransformers'

import Button from '../common/Button'
import Select from '../common/Select'

const Header = () => {
  const handleError = useHandleError()
  const { startQueue, stopQueue, isQueueStarted, loadingStates, currentServing } = useCall()

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    clearErrors,
    formState: { errors },
  } = useForm<StartQueueReqData>()

  const selectedBranch = watch('branch')
  const selectedCounter = watch('counter')
  const selectedCounterNumber = watch('counter_number')

  useEffect(() => {
    register('branch', { required: ERROR_MESSAGES.REQUIRED_COUNTER })
    register('counter', { required: ERROR_MESSAGES.REQUIRED_TYPE })
    register('counter_number', { required: ERROR_MESSAGES.REQUIRED_NUMBER })
  }, [register])

  useEffect(() => {
    if (selectedBranch) {
      setValue('counter', undefined)
      setValue('counter_number', undefined)
    }
  }, [selectedBranch, setValue])

  useEffect(() => {
    setValue('counter_number', undefined)
  }, [selectedCounter, setValue])

  const branchQuery = useQuery({
    queryKey: ['branches'],
    queryFn: counter.getBranches,
    select: transformBranches,
    onError: handleError,
  })

  const counterQuery = useQuery({
    queryKey: ['counters', selectedBranch?.value],
    queryFn: () => counter.getCounters(selectedBranch?.value as number),
    enabled: !!selectedBranch?.value,
    select: transformCounters,
    onError: handleError,
  })

  const onSubmit: SubmitHandler<StartQueueReqData> = (data) => {
    const { branch, counter, counter_number } = data
    if (branch && counter && counter_number) {
      const reqData: SocketReqJoinRoom = {
        location_id: branch.value,
        counter_id: counter.value,
        counter_type: counter.label,
        counter_number: counter_number.value,
      }
      startQueue(reqData)
    }
  }

  const isBranchDisabled = branchQuery.isLoading || isQueueStarted || loadingStates.start
  const isCounterDisabled = !selectedBranch || counterQuery.isLoading || isQueueStarted || loadingStates.start
  const isCounterNumberDisabled = !selectedCounter || isQueueStarted || loadingStates.start
  const isStartDisabled = isQueueStarted
  const isStopDisabled = !isQueueStarted || currentServing.isServing

  return (
    <header className="flex items-center justify-between bg-white px-4 py-2">
      <div className="inline-flex items-center gap-4">
        <img src="/jba-logo.svg" alt="JBA Logo" className="h-12" />
        <div className="text-xl font-bold text-jba-blue">JBA Indonesia</div>
      </div>
      <form className="inline-flex items-center gap-6" onSubmit={handleSubmit(onSubmit)}>
        <div className="inline-flex items-center gap-4">
          <div className="w-48">
            <Select
              placeholder="Pilih Cabang"
              icon="fluent:location-20-filled"
              options={branchQuery.data ?? []}
              isLoading={branchQuery.isLoading}
              isLocation
              selected={selectedBranch}
              setSelected={(value) => {
                setValue('branch', value)
                clearErrors('branch')
              }}
              disabled={isBranchDisabled}
              errorMessage={errors.branch?.message as string}
            />
          </div>
          <div className="w-48">
            <Select
              placeholder="Pilih Tipe Loket"
              icon="mdi:order-alphabetical-ascending"
              options={counterQuery.data ?? []}
              isLoading={selectedBranch && counterQuery.isLoading}
              isLocation
              selected={selectedCounter}
              setSelected={(value) => {
                setValue('counter', value)
                clearErrors('counter')
              }}
              disabled={isCounterDisabled}
              errorMessage={errors.counter?.message as string}
            />
          </div>
          <div className="w-48">
            <Select
              placeholder="Pilih Nomor Loket"
              icon="ph:list-numbers-fill"
              options={generateNumberOptions(1, 10)}
              isLocation
              selected={selectedCounterNumber}
              setSelected={(value) => {
                setValue('counter_number', value)
                clearErrors('counter_number')
              }}
              disabled={isCounterNumberDisabled}
              errorMessage={errors.counter_number?.message as string}
            />
          </div>
        </div>
        <div className="inline-flex h-6 w-0.5 rounded-full bg-gray-200" />
        <div className="inline-flex items-center gap-4">
          <Button
            type="submit"
            variant="primary"
            icon="material-symbols:play-circle-outline-rounded"
            isLoading={loadingStates.start}
            disabled={isStartDisabled}
          >
            Start Antrean
          </Button>
          <Button
            type="button"
            icon="material-symbols:power-rounded"
            variant="secondary"
            isLoading={loadingStates.stop}
            disabled={isStopDisabled}
            onClick={stopQueue}
          >
            Stop Antrean
          </Button>
        </div>
      </form>
    </header>
  )
}

export default Header
